import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGift } from "@fortawesome/free-solid-svg-icons";
import { useCart } from "../../../context/cartContext";
import { useAuth } from "../../../context/authContext";
import { useBonuses } from "../../../context/bonusContext";
import { lotteryService, promoService, formatApiError } from "../../../services/api";
import { getPurchaseLocation } from "../../../services/geolocation";
import styles from "./cartPage.module.css";
import { useNavigate } from "react-router-dom";
import { Button, EmptyState, ErrorBanner, FeedbackMessage, Modal } from "../../../components/ui";
import { AnimatePresence, motion } from "motion/react";
import { Reveal } from "../../../components/motion";
import { DURATION, EASE } from "../../../utils/motion";
import IdentityVerificationModal from "../../../components/identity/IdentityVerificationModal";
import useNowTick from "../../../hooks/useNowTick";
import useWelcomeBonus from "../../../hooks/useWelcomeBonus";
import { isCartItemBettingClosed } from "../apuestas/lotteryHelpers";
import CouponBox from "./CouponBox";
import {
  BONUS_KIND,
  assignBonuses,
  bonusAllowsLottery,
  bonusFitsFractions,
  formatBonusShortDate,
  fractionsNeededForBonus,
  isBonusEligibleForItem,
  itemFractions,
} from "../../../utils/bonusRules";

const formatCurrency = (value) => {
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

function itemAvailabilityKey(item) {
  return `${item.lottery || item.loteria}|${item.numero}|${item.serie || item.series}`;
}

/** Monto del backend ("4500.00") → número, o `fallback` si no es válido. */
function toAmount(value, fallback = null) {
  const n = Number.parseFloat(value);
  return Number.isFinite(n) ? n : fallback;
}

function itemValue(item) {
  return Number(item.precio ?? item.precioFraccion ?? 0) * itemFractions(item);
}

/** Fila de create_bet / promo validate (sin geolocalización). */
function toBetRow(item, bonusId) {
  const fractions = itemFractions(item);
  return {
    lottery: item.lottery || item.loteria,
    number: item.numero,
    series: item.serie || item.series,
    amount: itemValue(item),
    fractions,
    ...(bonusId ? { bonus_id: bonusId } : {}),
  };
}

/**
 * Pista cuando el usuario tiene un bono que no cabe en ninguna apuesta por la cantidad
 * de fracciones (p. ej. bono de 1 fracción y el ítem lleva 2).
 */
function bonusHintFor(item, { bonuses, assignment, items, maxAvailable }) {
  const assignedIds = new Set(Object.values(assignment));
  const candidate = bonuses.find(
    (b) =>
      !assignedIds.has(b.id) &&
      bonusAllowsLottery(b, item) &&
      !bonusFitsFractions(b, item) &&
      // Si otra apuesta del carrito ya puede usarlo, no insistir aquí.
      !items.some((other) => other.id !== item.id && isBonusEligibleForItem(b, other))
  );
  if (!candidate) return null;
  const needed = fractionsNeededForBonus(candidate, item);
  if (!needed) return null;
  const canAdjust = needed <= maxAvailable;
  let text;
  if (candidate.kind === BONUS_KIND.FRACTION) {
    text = "Tu bono es para 1 fracción: ajusta las fracciones a 1 para usarlo.";
  } else if (canAdjust) {
    text = `Tu bono es para el billete completo: ajusta las fracciones a ${needed} para usarlo.`;
  } else {
    text = `Tu bono es para el billete completo (${needed} fracciones) y este billete ya no las tiene todas disponibles. Prueba con otro número.`;
  }
  return { bonus: candidate, needed, canAdjust, text };
}

function effectiveMaxFractions(item) {
  const ticketMax = Number(item.maxFracciones) || 1;
  const avail = Number.parseInt(String(item.availableFractions ?? ""), 10);
  if (Number.isFinite(avail) && avail >= 0) {
    return Math.min(ticketMax, avail);
  }
  return ticketMax;
}

export default function CartPage() {
  const {
    items,
    removeItem,
    updateQuantity,
    setItemAvailability,
    clearCart,
    getTotal,
  } = useCart();
  const { isAuthenticated, user, updateBalance, loadProfile } = useAuth();
  const { available: bonuses, refresh: refreshBonuses } = useBonuses();
  const { welcome } = useWelcomeBonus();
  const navigate = useNavigate();
  const [showAuthWarning, setShowAuthWarning] = useState(false);
  const [isProcessingCheckout, setIsProcessingCheckout] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const [checkoutSuccess, setCheckoutSuccess] = useState("");
  const [needsIdentity, setNeedsIdentity] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  // itemId → bonusId | "balance" (elección explícita del usuario por apuesta)
  const [bonusChoices, setBonusChoices] = useState({});
  // Código de descuento aplicado: { code, data, order, orderSig, checking, error }
  const [promo, setPromo] = useState(null);
  const [purchaseResult, setPurchaseResult] = useState(null);
  const fetchGenRef = useRef(0);
  const now = useNowTick();

  const total = getTotal();
  const itemCount = items.reduce((acc, item) => acc + (item.cantidad || 1), 0);

  const refreshAvailability = useCallback(async () => {
    if (items.length === 0) return;
    const gen = ++fetchGenRef.current;
    setAvailabilityLoading(true);
    try {
      await Promise.all(
        items.map(async (item) => {
          const lottery = item.lottery || item.loteria;
          const number = item.numero;
          const series = item.serie || item.series;
          if (!lottery || !number || series == null || series === "") return;

          const result = await lotteryService.checkAvailability({
            lottery,
            number,
            series,
          });
          if (gen !== fetchGenRef.current) return;
          if (!result.ok) return;

          setItemAvailability(item.id, {
            availableFractions: result.data.available_fractions,
            isSoldOut: result.data.is_sold_out,
          });
        })
      );
    } finally {
      if (gen === fetchGenRef.current) {
        setAvailabilityLoading(false);
      }
    }
  }, [items, setItemAvailability]);

  // Reconsultar cuando cambian las combinaciones del carrito (no la cantidad).
  const itemsSignature = items
    .map((item) => itemAvailabilityKey(item))
    .join("||");

  useEffect(() => {
    refreshAvailability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsSignature]);

  const hasExpiredDraw = items.some((item) => isCartItemBettingClosed(item, now));

  const hasSoldOutOrOverLimit = items.some((item) => {
    if (isCartItemBettingClosed(item, now)) return false;
    if (item.isSoldOut) return true;
    const max = effectiveMaxFractions(item);
    return Number.isFinite(item.availableFractions) && (item.cantidad || 1) > max;
  });

  // ---- Bonos: una apuesta por bono, sugerido automáticamente y editable por el usuario ----
  const usableBonuses = useMemo(
    () => (isAuthenticated ? bonuses : []),
    [isAuthenticated, bonuses]
  );

  const assignment = useMemo(
    () =>
      assignBonuses(
        items,
        usableBonuses,
        bonusChoices,
        (item) =>
          isCartItemBettingClosed(item, now) ||
          Boolean(item.isSoldOut) ||
          effectiveMaxFractions(item) <= 0
      ),
    [items, usableBonuses, bonusChoices, now]
  );

  const setBonusChoice = useCallback((itemId, value) => {
    setBonusChoices((prev) => ({ ...prev, [itemId]: value }));
  }, []);

  /** Bonos que el usuario puede elegir para este ítem (no usados en otra apuesta). */
  const bonusOptionsFor = (item) =>
    usableBonuses.filter(
      (b) =>
        isBonusEligibleForItem(b, item) &&
        !Object.entries(assignment).some(([itemId, bonusId]) => bonusId === b.id && itemId !== item.id)
    );

  const betRows = useMemo(
    () => items.map((item) => toBetRow(item, assignment[item.id])),
    [items, assignment]
  );
  const rowsSignature = useMemo(() => JSON.stringify(betRows), [betRows]);

  const bonusSavings = items.reduce(
    (acc, item) => (assignment[item.id] ? acc + itemValue(item) : acc),
    0
  );
  const eligibleAmount = Math.max(0, total - bonusSavings);

  // ---- Código de descuento: recalcular la vista previa cuando cambia la canasta ----
  useEffect(() => {
    if (!promo || promo.orderSig === rowsSignature || items.length === 0) return undefined;
    let cancelled = false;
    const code = promo.code;
    setPromo((p) => (p && p.code === code ? { ...p, checking: true } : p));
    const timer = setTimeout(async () => {
      const res = await promoService.validate({ code, amount: eligibleAmount, bets: betRows });
      if (cancelled) return;
      setPromo((p) => {
        if (!p || p.code !== code) return p;
        if (res.ok && res.data?.applies_in !== "REDEEM") {
          return {
            ...p,
            data: res.data,
            order: res.data?.order || null,
            orderSig: rowsSignature,
            checking: false,
            error: "",
          };
        }
        return {
          ...p,
          order: null,
          orderSig: rowsSignature,
          checking: false,
          error: res.ok
            ? "Este código no es un descuento."
            : res.message || "Este código ya no aplica a tu canasta.",
        };
      });
    }, 450);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promo?.code, promo?.orderSig, rowsSignature]);

  const promoApplied = Boolean(
    promo && !promo.error && !promo.checking && promo.orderSig === rowsSignature
  );
  const order = promoApplied ? promo.order : null;

  // Totales: con código se usa la vista previa exacta del backend (misma lógica que create_bet).
  const grossShown = order ? toAmount(order.gross_amount, total) : total;
  const bonusShown = order ? toAmount(order.bonus_amount, bonusSavings) : bonusSavings;
  const discountShown = order
    ? toAmount(order.discount_amount, 0)
    : promoApplied
      ? toAmount(promo.data?.benefit_amount, 0)
      : 0;
  const chargedShown = order
    ? toAmount(order.charged_amount, Math.max(0, grossShown - bonusShown - discountShown))
    : Math.max(0, total - bonusSavings - discountShown);

  const handleRedeemed = useCallback(
    (data) => {
      const balance = toAmount(data?.new_balance);
      if (balance != null) updateBalance(balance);
      loadProfile();
      refreshBonuses();
    },
    [updateBalance, loadProfile, refreshBonuses]
  );

  const handleCheckout = async () => {
    setCheckoutError("");
    setCheckoutSuccess("");
    setNeedsIdentity(false);

    if (!isAuthenticated) {
      setShowAuthWarning(true);
      return;
    }

    if (items.length === 0) return;

    if (hasExpiredDraw) {
      setCheckoutError(
        "Hay apuestas cuyo sorteo ya se realizó. Elimínalas del carrito antes de comprar."
      );
      return;
    }

    if (hasSoldOutOrOverLimit) {
      setCheckoutError(
        "Hay billetes sin fracciones suficientes. Ajusta las cantidades o quita los agotados."
      );
      await refreshAvailability();
      return;
    }

    if (promo && !promoApplied) {
      setCheckoutError(
        promo.checking
          ? "Estamos recalculando tu descuento. Inténtalo en un momento."
          : `El código ${promo.code} no aplica a tu canasta. Quítalo o ajusta tus apuestas para continuar.`
      );
      return;
    }

    setIsProcessingCheckout(true);
    try {
      // Geolocalización OBLIGATORIA del lugar de la compra: si el usuario no
      // aprueba el permiso (o el navegador no la da), no se permite comprar.
      const loc = await getPurchaseLocation();
      if (!loc) {
        setIsProcessingCheckout(false);
        setCheckoutError(
          "Para comprar debes permitir el acceso a tu ubicación. Actívala en tu navegador (o revisa los permisos del sitio) e inténtalo de nuevo."
        );
        return;
      }

      const payload = betRows.map((row) => ({ ...row, ...loc }));

      const result = await lotteryService.createBet(payload, {
        promoCode: promoApplied ? promo.code : undefined,
      });
      if (!result.ok) {
        const d = result.data || {};
        if (d.error === "identity_not_verified") {
          setNeedsIdentity(true);
          setCheckoutError(
            d.message ||
              "Debes verificar tu identidad antes de comprar. Sube las fotos de tu cédula para continuar."
          );
          return;
        }
        if (d.error === "token_expired") {
          setCheckoutError(
            d.message ||
              "Tu sesión expiró. Inicia sesión de nuevo e intenta la compra."
          );
          return;
        }
        const msg = formatApiError(d) || "No fue posible completar la compra.";
        setCheckoutError(msg);
        if (
          typeof msg === "string" &&
          (msg.includes("fracciones disponibles") ||
            msg.includes("No quedan fracciones"))
        ) {
          await refreshAvailability();
        }
        if (typeof msg === "string" && /bono/i.test(msg)) {
          // Bono usado/vencido en otra sesión: refrescar para recalcular la canasta.
          refreshBonuses();
        }
        return;
      }

      const d = result.data || {};
      const summary = d.summary || {};
      const charged = toAmount(
        d.charged_amount ?? summary.charged_amount ?? d.total_amount,
        chargedShown
      );
      const newBalance =
        toAmount(d.new_balance) ??
        toAmount(d.remaining_balance?.amount) ??
        toAmount(d.user?.balance?.amount);
      if (newBalance != null) {
        updateBalance(newBalance);
      }
      await loadProfile();
      if (Object.keys(assignment).length > 0 || summary.bonuses_used?.length) {
        refreshBonuses();
      }

      setPurchaseResult({
        betsCount: Array.isArray(d.bets) ? d.bets.length : items.length,
        gross: toAmount(summary.gross_amount ?? d.gross_amount, grossShown),
        bonusAmount: toAmount(summary.bonus_amount, bonusShown),
        discountAmount: toAmount(summary.discount_amount, discountShown),
        charged,
        promoCode: summary.promo?.code || (promoApplied ? promo.code : null),
        bonusesUsed: Array.isArray(summary.bonuses_used) ? summary.bonuses_used : [],
        newBalance,
      });
      clearCart();
      setBonusChoices({});
      setPromo(null);
    } catch (error) {
      setCheckoutError("Error de conexión al procesar la compra.");
    } finally {
      setIsProcessingCheckout(false);
    }
  };

  const handleGoToLogin = () => {
    navigate("/auth", {
      state: {
        from: "/carrito",
        message: "Inicia sesión para completar tu compra",
      },
    });
  };

  const balance = toAmount(user?.balance?.amount ?? user?.balance);
  const canUseReferral = isAuthenticated && welcome?.eligible !== false;

  const checkoutLabel = isProcessingCheckout
    ? "Procesando compra..."
    : hasExpiredDraw
      ? "Hay sorteos cerrados"
      : hasSoldOutOrOverLimit
        ? "Hay billetes agotados"
        : chargedShown <= 0 && bonusShown > 0
          ? "Confirmar compra con mi bono"
          : `Pagar ${formatCurrency(chargedShown)}`;

  return (
    <div className={styles.page}>
      <div
        className={
          items.length === 0 ? styles.containerEmpty : styles.container
        }
      >
        <div className={styles.cartSection}>
          <div className={styles.cartHeader}>
            <h2 className={styles.sectionTitle}>Tus billetes</h2>
            {items.length > 0 && (
              <button className={styles.clearBtn} onClick={clearCart}>
                Vaciar carrito
              </button>
            )}
          </div>

          {items.length > 0 && usableBonuses.length > 0 && (
            <div className={styles.bonusBanner} role="status">
              <FontAwesomeIcon icon={faGift} className={styles.bonusBannerIcon} />
              <p>
                {usableBonuses.length === 1
                  ? `Tienes 1 bono disponible (${usableBonuses[0].label}).`
                  : `Tienes ${usableBonuses.length} bonos disponibles.`}{" "}
                Elige <strong>Usar mi bono</strong> en el billete donde quieras usarlo.
              </p>
            </div>
          )}

          <div className={styles.itemsList}>
            {items.length === 0 ? (
              <EmptyState
                title="Tu carrito está vacío"
                description="Agrega billetes de lotería para comenzar."
                actionLabel="Ver loterías"
                onAction={() => navigate("/apuestas")}
                icon="🛒"
              />
            ) : (
              <AnimatePresence initial={false} mode="popLayout">
                {items.map((item) => {
                  const max = effectiveMaxFractions(item);
                  const qty = item.cantidad || 1;
                  const expired = isCartItemBettingClosed(item, now);
                  const soldOut = Boolean(item.isSoldOut) || max <= 0;
                  const hasAvail = Number.isFinite(item.availableFractions);
                  const assignedId = assignment[item.id] || null;
                  const bonusOptions =
                    !expired && !soldOut ? bonusOptionsFor(item) : [];
                  const hint =
                    !assignedId && bonusOptions.length === 0 && !expired && !soldOut
                      ? bonusHintFor(item, {
                          bonuses: usableBonuses,
                          assignment,
                          items,
                          maxAvailable: max,
                        })
                      : null;

                  return (
                    <motion.div
                      key={item.id}
                      layout="position"
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: expired ? 0.55 : 1, y: 0 }}
                      exit={{ opacity: 0, x: -24, transition: { duration: DURATION.fast, ease: EASE.inOut } }}
                      transition={{ duration: DURATION.normal, ease: EASE.out }}
                      className={`${styles.item} ${expired ? styles.itemExpired : ""} ${
                        assignedId ? styles.itemWithBonus : ""
                      }`}
                    >
                      <div className={styles.itemImage}>
                        {item.imagen ? (
                          <img src={item.imagen} alt={item.loteria} />
                        ) : (
                          <div className={styles.lotteryIcon}>&#127920;</div>
                        )}
                      </div>

                      <div className={styles.itemInfo}>
                        <div className={styles.itemMeta}>
                          <span className={styles.itemType}>
                            Billete de Loteria
                          </span>
                          <div className={styles.itemActions}>
                            <button
                              className={styles.deleteBtn}
                              onClick={() => removeItem(item.id)}
                            >
                              &#128465;
                            </button>
                          </div>
                        </div>
                        <h3 className={styles.itemName}>{item.loteria}</h3>
                        <div className={styles.itemDetails}>
                          <span>
                            Numero: <strong>{item.numero}</strong>
                          </span>
                          <span>
                            Serie: <strong>{item.series}</strong>
                          </span>
                        </div>
                        {expired ? (
                          <p className={styles.expiredHint}>
                            Esta rifa ya se jugó. Elimínala del carrito.
                          </p>
                        ) : hasAvail ? (
                          <p
                            className={
                              soldOut
                                ? styles.availabilitySoldOut
                                : styles.availabilityHint
                            }
                          >
                            {soldOut
                              ? "Agotado"
                              : `Quedan ${item.availableFractions}`}
                          </p>
                        ) : null}
                      </div>

                      <div className={styles.itemRight}>
                        <div className={styles.quantityControl}>
                          <button
                            type="button"
                            onClick={() => updateQuantity(item.id, qty - 1)}
                            disabled={expired || qty <= 1 || soldOut}
                            aria-label="Quitar fracción"
                          >
                            -
                          </button>
                          <span>{qty}</span>
                          <button
                            type="button"
                            onClick={() => updateQuantity(item.id, qty + 1)}
                            disabled={
                              expired || soldOut || qty >= Math.max(max, 1)
                            }
                            aria-label="Agregar fracción"
                          >
                            +
                          </button>
                        </div>
                        {assignedId ? (
                          <div className={styles.itemPriceBonus}>
                            <span className={styles.bonusTag}>
                              <FontAwesomeIcon icon={faGift} /> Gratis con bono
                            </span>
                            <span className={styles.itemPriceOriginal}>
                              {formatCurrency((item.precio || 0) * qty)}
                            </span>
                            <span className={styles.itemPrice}>{formatCurrency(0)}</span>
                          </div>
                        ) : (
                          <span className={styles.itemPrice}>
                            {formatCurrency((item.precio || 0) * qty)}
                          </span>
                        )}
                      </div>

                      {(bonusOptions.length > 0 || hint) && (
                        <div className={styles.itemBonusRow}>
                          {bonusOptions.length > 0 && (
                            <fieldset className={styles.payChoice}>
                              <legend className={styles.payChoiceLegend}>
                                ¿Cómo quieres pagar este billete?
                              </legend>
                              <label
                                className={`${styles.payOption} ${
                                  !assignedId ? styles.payOptionActive : ""
                                }`}
                              >
                                <input
                                  type="radio"
                                  name={`pay-${item.id}`}
                                  checked={!assignedId}
                                  onChange={() => setBonusChoice(item.id, "balance")}
                                />
                                <span>Pagar con saldo</span>
                              </label>
                              {bonusOptions.map((bonus) => (
                                <label
                                  key={bonus.id}
                                  className={`${styles.payOption} ${
                                    assignedId === bonus.id ? styles.payOptionActive : ""
                                  }`}
                                >
                                  <input
                                    type="radio"
                                    name={`pay-${item.id}`}
                                    checked={assignedId === bonus.id}
                                    onChange={() => setBonusChoice(item.id, bonus.id)}
                                  />
                                  <span>
                                    <FontAwesomeIcon icon={faGift} className={styles.payOptionIcon} />
                                    Usar mi bono · {bonus.label}
                                    {bonus.expires_at
                                      ? ` · vence el ${formatBonusShortDate(bonus.expires_at)}`
                                      : ""}
                                  </span>
                                </label>
                              ))}
                            </fieldset>
                          )}

                          {hint && (
                            <div className={styles.bonusHint}>
                              <p>
                                <FontAwesomeIcon icon={faGift} /> {hint.text}
                              </p>
                              {hint.canAdjust && (
                                <button
                                  type="button"
                                  className={styles.bonusHintBtn}
                                  onClick={() => {
                                    updateQuantity(item.id, hint.needed);
                                    setBonusChoice(item.id, hint.bonus.id);
                                  }}
                                >
                                  Ajustar
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            )}
          </div>
        </div>

        {items.length > 0 && (
          <Reveal variant="sharedAxisX" className={styles.summarySection}>
            <div className={styles.summaryCard}>
              <h2>Resumen</h2>

              {showAuthWarning && (
                <div className={styles.authWarning}>
                  <p>Debes iniciar sesión para completar la compra</p>
                  <button
                    className={styles.loginBtn}
                    onClick={handleGoToLogin}
                  >
                    Iniciar sesión
                  </button>
                </div>
              )}
              {checkoutError && (
                <ErrorBanner
                  message={checkoutError}
                  className={styles.checkoutError}
                  onRetry={needsIdentity ? () => setVerifyOpen(true) : undefined}
                  retryLabel="Verificar mi identidad"
                />
              )}
              <FeedbackMessage type="success" className={styles.checkoutError}>
                {checkoutSuccess}
              </FeedbackMessage>

              <div className={styles.summaryDetails}>
                <div className={styles.summaryRow}>
                  <span>
                    Subtotal ({itemCount} {itemCount === 1 ? "fracción" : "fracciones"})
                  </span>
                  <span>{formatCurrency(grossShown)}</span>
                </div>
                {bonusShown > 0 && (
                  <div className={`${styles.summaryRow} ${styles.summaryRowSaving}`}>
                    <span>Ahorro con bono</span>
                    <span>−{formatCurrency(bonusShown)}</span>
                  </div>
                )}
                {promo && (promoApplied || promo.checking) && (
                  <div className={`${styles.summaryRow} ${styles.summaryRowSaving}`}>
                    <span>Descuento ({promo.code})</span>
                    <span>
                      {promo.checking ? "Calculando…" : `−${formatCurrency(discountShown)}`}
                    </span>
                  </div>
                )}
              </div>

              <div className={styles.totalRow}>
                <span>Total a pagar:</span>
                <span className={styles.totalValue}>
                  {formatCurrency(chargedShown)}
                </span>
              </div>
              {isAuthenticated && balance != null && (
                <p className={styles.balanceNote}>
                  Se descuenta de tu saldo ({formatCurrency(balance)} disponibles).
                </p>
              )}

              <CouponBox
                isAuthenticated={isAuthenticated}
                onRequireLogin={() => setShowAuthWarning(true)}
                rows={betRows}
                eligibleAmount={eligibleAmount}
                promo={promo}
                onApply={(applied) =>
                  setPromo({
                    ...applied,
                    orderSig: rowsSignature,
                    checking: false,
                    error: "",
                  })
                }
                onRemove={() => setPromo(null)}
                onRedeemed={handleRedeemed}
                canUseReferral={canUseReferral}
              />

              <button
                className={styles.checkoutBtn}
                onClick={handleCheckout}
                disabled={
                  isProcessingCheckout ||
                  hasSoldOutOrOverLimit ||
                  hasExpiredDraw ||
                  availabilityLoading ||
                  Boolean(promo?.checking)
                }
              >
                {checkoutLabel}
              </button>

              <div className={styles.secureNote}>
                <span>&#128274;</span>
                <span>Compra 100% segura</span>
              </div>
            </div>
          </Reveal>
        )}
      </div>

      <IdentityVerificationModal
        isOpen={verifyOpen}
        onClose={() => setVerifyOpen(false)}
        onVerified={() => {
          setNeedsIdentity(false);
          setCheckoutError("");
          setCheckoutSuccess(
            "¡Identidad verificada! Ya puedes finalizar tu compra."
          );
        }}
      />

      <Modal
        isOpen={Boolean(purchaseResult)}
        onClose={() => setPurchaseResult(null)}
        title="¡Compra realizada!"
        size="sm"
      >
        {purchaseResult && (
          <div className={styles.receipt}>
            <p className={styles.receiptLead}>
              {purchaseResult.betsCount === 1
                ? "Tu billete quedó registrado."
                : `Tus ${purchaseResult.betsCount} billetes quedaron registrados.`}{" "}
              Puedes verlos en Mis tickets.
            </p>
            <dl className={styles.receiptList}>
              <div>
                <dt>Valor de las apuestas</dt>
                <dd>{formatCurrency(purchaseResult.gross)}</dd>
              </div>
              {purchaseResult.bonusAmount > 0 && (
                <div className={styles.receiptSaving}>
                  <dt>
                    Pagado con bono
                    {purchaseResult.bonusesUsed.length > 0
                      ? ` (${purchaseResult.bonusesUsed.map((b) => b.label).filter(Boolean).join(", ")})`
                      : ""}
                  </dt>
                  <dd>−{formatCurrency(purchaseResult.bonusAmount)}</dd>
                </div>
              )}
              {purchaseResult.discountAmount > 0 && (
                <div className={styles.receiptSaving}>
                  <dt>
                    Descuento{purchaseResult.promoCode ? ` (${purchaseResult.promoCode})` : ""}
                  </dt>
                  <dd>−{formatCurrency(purchaseResult.discountAmount)}</dd>
                </div>
              )}
              <div className={styles.receiptTotal}>
                <dt>Total cobrado</dt>
                <dd>{formatCurrency(purchaseResult.charged)}</dd>
              </div>
              {purchaseResult.newBalance != null && (
                <div>
                  <dt>Tu nuevo saldo</dt>
                  <dd>{formatCurrency(purchaseResult.newBalance)}</dd>
                </div>
              )}
            </dl>
            <div className={styles.receiptActions}>
              <Button variant="primary" fullWidth onClick={() => navigate("/historial")}>
                Ver mis boletos
              </Button>
              <Button variant="ghost" fullWidth onClick={() => navigate("/apuestas")}>
                Seguir comprando
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
